import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  ArrowLeft, ExternalLink, CheckCircle2, Loader2, RefreshCw, ClipboardList,
  AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Talent Lead Capture (V412 — 2026-02-13)
 *
 * Full-screen embedded view of the client's lead-capture form
 * (Zapier / Typeform / etc). Talent submit consumer leads from here
 * during an active shift.
 *
 * Behaviour:
 *  - GET /talent/bookings/:bookingId/lead-capture — pull the client's
 *    URL + submission count.
 *  - Try iframe embed first. Detect iframe load-blocked → surface a
 *    fallback "Open Form in Browser" button + "I submitted a lead"
 *    confirmation button (that hits /log).
 *  - Post-log: keep talent on this screen with:
 *      * "Submit Another Lead" — reloads iframe (or reopens tab)
 *      * "Back to Shift"       — returns to /talent/checkin/:bookingId
 *  - We NEVER store the actual customer form values — only the metadata
 *    that a submission was made.
 */
const IFRAME_LOAD_TIMEOUT_MS = 4000;

const LeadCapture = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeStatus, setIframeStatus] = useState('loading'); // loading | ok | blocked
  const [logging, setLogging] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const iframeRef = useRef(null);
  const loadTimerRef = useRef(null);

  const backToShift = useCallback(() => {
    navigate(`/talent/checkin/${bookingId}`);
  }, [bookingId, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/talent/bookings/${bookingId}/lead-capture`);
      setConfig(res.data);
      if (!res.data.enabled) {
        toast.info('Lead capture is not configured for this client.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load lead-capture config');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { load(); }, [load]);

  // Iframe load / block detection: some clients (Zapier) send
  // `X-Frame-Options: DENY` — the iframe will silently stay blank.
  // We start a timer on mount and mark it "blocked" if `onLoad`
  // never fires. If onLoad fires, we mark it "ok".
  useEffect(() => {
    if (!config?.url) return undefined;
    setIframeStatus('loading');
    if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    loadTimerRef.current = setTimeout(() => {
      setIframeStatus((prev) => (prev === 'loading' ? 'blocked' : prev));
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => {
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, [config?.url, refreshKey]);

  const handleIframeLoad = () => {
    // Note: browsers still fire `load` on blocked iframes in many
    // cases. This flip prevents the fallback from showing when the
    // form DOES actually render.
    setIframeStatus('ok');
  };

  const submitAnother = () => {
    setRefreshKey((k) => k + 1);
    if (iframeStatus === 'blocked' && config?.url) {
      window.open(config.url, '_blank', 'noopener,noreferrer');
    }
  };

  const openInNewTab = () => {
    if (!config?.url) return;
    window.open(config.url, '_blank', 'noopener,noreferrer');
  };

  const logSubmission = async () => {
    setLogging(true);
    try {
      const res = await api.post(
        `/talent/bookings/${bookingId}/lead-capture/log`,
        { success: true },
      );
      toast.success(`Lead ${res.data.talent_submission_count} logged`);
      // Refresh counts.
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to log submission');
    } finally {
      setLogging(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!config?.enabled) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="lead-capture-disabled">
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={backToShift} data-testid="back-to-shift-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-slate-900">Capture Lead</h1>
          </div>
        </header>
        <main className="max-w-lg mx-auto px-4 py-8">
          <Card>
            <CardContent className="py-12 text-center">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">Lead capture is not configured for this event.</p>
              <p className="text-sm text-slate-400">
                {config?.client_name ? `${config.client_name} has not provided a lead-capture form.` : 'The client has not provided a lead-capture form.'}
              </p>
              <Button className="mt-6" onClick={backToShift} data-testid="back-to-shift-empty">Back to Shift</Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="lead-capture-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={backToShift} data-testid="back-to-shift-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base font-bold text-slate-900 truncate">
                {config.client_name || 'Client'} Lead Capture
              </h1>
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Consumer data goes directly to the client — nothing is stored on iEvntür.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="text-xs" data-testid="submission-count-badge">
              {config.submission_count} submitted
            </Badge>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-4xl w-full mx-auto p-3 sm:p-4 flex flex-col gap-3">
        {iframeStatus === 'blocked' && (
          <Alert className="border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-sm text-amber-900">
              This form can&apos;t be embedded here. Tap <strong>Open Form</strong> to fill it in
              your browser — then come back and tap <strong>I submitted a lead</strong> so we can
              log it against your shift.
            </AlertDescription>
          </Alert>
        )}

        {iframeStatus !== 'blocked' && (
          <div
            className="flex-1 rounded-xl overflow-hidden border border-slate-200 bg-white"
            style={{ minHeight: '60vh' }}
            data-testid="lead-capture-iframe-wrap"
          >
            <iframe
              key={refreshKey}
              ref={iframeRef}
              title={`${config.client_name || 'Client'} Lead Capture Form`}
              src={config.url}
              className="w-full h-full min-h-[60vh]"
              onLoad={handleIframeLoad}
              // Sandbox is intentionally permissive — the customer form
              // needs to submit + navigate on its own domain. Cross-
              // origin isolation is enforced by the browser regardless.
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
              data-testid="lead-capture-iframe"
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sticky bottom-0 bg-slate-50 pt-2 pb-3">
          <Button
            variant="outline"
            onClick={openInNewTab}
            className="w-full"
            data-testid="open-in-new-tab-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open Form
          </Button>
          <Button
            onClick={logSubmission}
            disabled={logging}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="log-submission-btn"
          >
            {logging ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            I submitted a lead
          </Button>
          <Button
            variant="secondary"
            onClick={submitAnother}
            className="w-full"
            data-testid="submit-another-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Submit Another Lead
          </Button>
        </div>
      </main>
    </div>
  );
};

export default LeadCapture;
